package addedittravelagent.components

import addedittravelagent.AddEditTravelAgentEvent
import addedittravelagent.AddEditTravelAgentState
import alltravelagents.components.TravelAgentCommissionTypeDropDown
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import com.essntl.core.unsplash.ui.UnsplashImage
import com.essntl.features.file.ui.essntlgallery.EssntlGallery
import com.essntl.features.file.ui.essntlgallery.EssntlGalleryState
import com.essntl.features.travelagent.domain.model.TravelAgentModel
import com.essntl.uikit.components.button.tinted.EsMediumTintedButton
import com.essntl.uikit.components.file.FileViewer
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.input.EsPhoneInput
import com.essntl.uikit.components.input.label.EsInputLabel
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.validation.EsInputState

@Composable
fun TravelAgentBaseInformation(
    state: AddEditTravelAgentState,
    onEvent: (AddEditTravelAgentEvent) -> Unit,
    onTravelAgentChange: (TravelAgentModel) -> Unit,
    enabled: Boolean,
    firstNameInputState: EsInputState,
    validateFirstName: (String) -> Unit,
) {
    var openEssntlGalleryDialog by rememberSaveable { mutableStateOf(false) }

    val travelAgent = state.travelAgent.value
    val isLoading = state.addEditTravelAgent.isLoading

    var isExpanded by rememberSaveable { mutableStateOf(true) }

    var fileUrl by rememberSaveable { mutableStateOf("") }
    var isFileViewerOpened by rememberSaveable { mutableStateOf(false) }

    Column(
        modifier =
            Modifier
                .fillMaxWidth()
                .background(
                    color = MaterialTheme.esColorScheme.bgSecondary,
                    shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
                )
                .padding(
                    vertical = 3.spacing,
                    horizontal = 2.5f.spacing,
                ),
    ) {
        Row(
            horizontalArrangement = Arrangement.SpaceBetween,
            modifier =
                Modifier
                    .fillMaxWidth(),
        ) {
            Text(
                text = "Base Information",
                color = MaterialTheme.esColorScheme.textPrimary,
                style = MaterialTheme.esTypography.s2SemiBold16,
            )
            EsLargeIcon(
                res = EsIcons.ArrowDown,
                modifier =
                    Modifier
                        .graphicsLayer {
                            transformOrigin = TransformOrigin.Center
                            rotationZ = if (isExpanded) 180f else 0f
                        }
                        .clip(CircleShape)
                        .pointerHoverIcon(PointerIcon.Hand)
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                        ) {
                            isExpanded = !isExpanded
                        },
            )
        }

        AnimatedVisibility(isExpanded) {
            Column(
                modifier =
                    Modifier
                        .fillMaxWidth(),
            ) {
                Spacer(modifier = Modifier.height(3.spacing))

                EsInput(
                    value = travelAgent.contact?.firstName.orEmpty(),
                    onValueChange = {
                        validateFirstName(it)
                        onTravelAgentChange(
                            travelAgent.copy(
                                contact = travelAgent.contact?.copy(firstName = it),
                            ),
                        )
                    },
                    state = firstNameInputState,
                    placeholder = "Enter first name",
                    message = "This field is required",
                    label = "First name",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = travelAgent.contact?.lastName.orEmpty(),
                    onValueChange = {
                        onTravelAgentChange(
                            travelAgent.copy(
                                contact = travelAgent.contact?.copy(lastName = it),
                            ),
                        )
                    },
                    placeholder = "Enter last name",
                    label = "Last name",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(2.spacing),
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    EsInput(
                        value = travelAgent.commission?.let { if (it > 0) "$it" else "" } ?: "",
                        placeholder = "Commission %",
                        label = "Enter the commission %",
                        enabled = enabled && !isLoading,
                        onValueChange = {
                            val commission: Number = it.toDoubleOrNull() ?: 0
                            onTravelAgentChange(
                                travelAgent.copy(
                                    commission = commission.toDouble(),
                                ),
                            )
                        },
                        keyboardOptions =
                            KeyboardOptions(
                                keyboardType = KeyboardType.Number,
                            ),
                        modifier = Modifier.weight(3f),
                    )

                    TravelAgentCommissionTypeDropDown(
                        commissionType = travelAgent.commissionType,
                        onCommissionTypeSelected = { newCommissionType ->
                            if (newCommissionType != travelAgent.commissionType) {
                                onTravelAgentChange(
                                    travelAgent.copy(
                                        commissionType = newCommissionType,
                                    ),
                                )
                            }
                        },
                        enabled =
                            travelAgent.commission?.takeIf { it.toInt() != 0 }?.toString()?.isNotEmpty()
                                ?: false &&
                                enabled && !isLoading,
                        modifier =
                            Modifier
                                .padding(top = 3f.spacing)
                                .weight(1f),
                    )
                }

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = travelAgent.contact?.organization?.name.orEmpty(),
                    onValueChange = {
                        onTravelAgentChange(
                            travelAgent.copy(
                                contact = travelAgent.contact?.copy(
                                    organization = travelAgent.contact?.organization?.copy(
                                        name = it,
                                    ),
                                ),
                            ),
                        )
                    },
                    placeholder = "Enter Agency",
                    label = "Agency",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = travelAgent.contact?.email.orEmpty(),
                    onValueChange = {
                        onTravelAgentChange(
                            travelAgent.copy(
                                contact = travelAgent.contact?.copy(
                                    email = it,
                                ),
                            ),
                        )
                    },
                    placeholder = "Enter Email",
                    label = "Email",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInputLabel(
                    label = "Logo",
                    modifier = Modifier,
                )

                Spacer(
                    modifier = Modifier.height(.5f.spacing),
                )

                state.fileState.getAllFile.data?.firstOrNull()?.let { file ->
                    UnsplashImage(
                        url = file.url,
                        selectable = false,
                        imageSize = DpSize(264.dp, 160.dp),
                        enabled = enabled && !isLoading,
                        onDelete = {
                            onEvent(AddEditTravelAgentEvent.DeleteLogo)
                        },
                        onClick = {
                            isFileViewerOpened = true
                            fileUrl = file.url
                        },
                    )
                } ?: EsMediumTintedButton(
                    text = "Odia Gallery",
                    enabled = enabled && !isLoading,
                    onClick = { openEssntlGalleryDialog = true },
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = travelAgent.contact?.organization?.website.orEmpty(),
                    onValueChange = {
                        travelAgent.copy(
                            contact = travelAgent.contact?.copy(
                                organization = travelAgent.contact?.organization?.copy(
                                    website = it,
                                ),
                            ),
                        )
                    },
                    placeholder = "Enter Website",
                    label = "Website",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = travelAgent.contact?.address?.city.orEmpty(),
                    onValueChange = {
                        onTravelAgentChange(
                            travelAgent.copy(
                                contact = travelAgent.contact?.copy(
                                    address = travelAgent.contact?.address?.copy(
                                        city = it,
                                    ),
                                ),
                            ),
                        )
                    },
                    placeholder = "Enter City",
                    label = "City",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = travelAgent.contact?.address?.country.orEmpty(),
                    onValueChange = {
                        onTravelAgentChange(
                            travelAgent.copy(
                                contact = travelAgent.contact?.copy(
                                    address = travelAgent.contact?.address?.copy(
                                        country = it,
                                    ),
                                ),
                            ),
                        )
                    },
                    placeholder = "Enter Country",
                    label = "Country",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = travelAgent.contact?.address?.line1.orEmpty(),
                    onValueChange = {
                        onTravelAgentChange(
                            travelAgent.copy(
                                contact = travelAgent.contact?.copy(
                                    address = travelAgent.contact?.address?.copy(
                                        line1 = it,
                                    ),
                                ),
                            ),
                        )
                    },
                    placeholder = "Enter Address",
                    label = "Address Line",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = travelAgent.contact?.address?.zipCode.orEmpty(),
                    onValueChange = {
                        onTravelAgentChange(
                            travelAgent.copy(
                                contact = travelAgent.contact?.copy(
                                    address = travelAgent.contact?.address?.copy(
                                        zipCode = it,
                                    ),
                                ),
                            ),
                        )
                    },
                    placeholder = "Enter Zip Code",
                    label = "Zip Code",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = travelAgent.jobTitle ?: "",
                    onValueChange = {
                        onTravelAgentChange(
                            travelAgent.copy(
                                jobTitle = it,
                            ),
                        )
                    },
                    placeholder = "Enter JobTitle",
                    label = "JobTitle",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = travelAgent.contact?.notes.orEmpty(),
                    onValueChange = {
                        onTravelAgentChange(
                            travelAgent.copy(
                                contact = travelAgent.contact?.copy(
                                    notes = it,
                                ),
                            ),
                        )
                    },
                    placeholder = "Enter Notes",
                    label = "Notes",
                    singleLine = false,
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = travelAgent.source ?: "",
                    onValueChange = {
                        onTravelAgentChange(
                            travelAgent.copy(
                                source = it,
                            ),
                        )
                    },
                    placeholder = "Enter Source",
                    label = "Source",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsPhoneInput(
                    value = travelAgent.contact?.phoneNumber?.toLongOrNull() ?: 0,
                    onValueChange = { value ->
                        onTravelAgentChange(
                            travelAgent.copy(
                                contact = travelAgent.contact?.copy(
                                    phoneNumber = value.toString(),
                                ),
                            ),
                        )
                    },
                    placeholder = "Enter Telephone",
                    label = "Telephone",
                    enabled = enabled && !isLoading,
                )
            }
        }
    }

    EssntlGallery(
        entity = EssntlGalleryState.Entity.TA_LOGOS,
        isOpen = openEssntlGalleryDialog,
        onDismissRequest = {
            openEssntlGalleryDialog = false
        },
        multipleSelect = false,
        onAddImages = {
            val image = it.firstOrNull() ?: return@EssntlGallery
            onEvent(AddEditTravelAgentEvent.AddLogo(image))
        },
    )

    FileViewer(
        fileUrl = fileUrl,
        isDialogOpened = isFileViewerOpened,
        onDismissRequest = {
            isFileViewerOpened = false
            fileUrl = ""
        },
    )
}
